import { FormEvent, useEffect, useMemo, useState } from 'react';
import { backendApi } from '../lib/api';
import { useAuth } from '../lib/auth';
import { StatCard, Icon } from '../components/Icon';

interface Campaign {
  id: string; name?: string; subject?: string; body?: string; segment?: string;
  status?: string; recipient_count?: number; sent_count?: number; run_count?: number;
  recurrence?: string; scheduled_at?: string; created_at?: string;
}
interface LogEntry {
  id?: string; to_email?: string; recipient?: string; subject?: string;
  type?: string; status?: string; sent_at?: string; created_at?: string;
}
/** "5 Sept 2026 - 9:15 am" — en-GB gives "Sept"; time lower-cased to am/pm. */
function fmtSentFull(d: Date): string {
  const date = d.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' });
  const time = d.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', hour12: true }).toLowerCase();
  return `${date} - ${time}`;
}
/** Verbose relative time, e.g. "10 days ago". */
function timeAgoLong(d: Date): string {
  const secs = Math.max(0, (Date.now() - d.getTime()) / 1000);
  if (secs < 60) return 'just now';
  const m = Math.floor(secs / 60); if (m < 60) return `${m} minute${m === 1 ? '' : 's'} ago`;
  const h = Math.floor(m / 60); if (h < 24) return `${h} hour${h === 1 ? '' : 's'} ago`;
  const days = Math.floor(h / 24); if (days < 30) return `${days} day${days === 1 ? '' : 's'} ago`;
  const mo = Math.floor(days / 30); if (mo < 12) return `${mo} month${mo === 1 ? '' : 's'} ago`;
  const y = Math.floor(days / 365); return `${y} year${y === 1 ? '' : 's'} ago`;
}

interface Segment { id: string; name?: string; description?: string; count?: number }
interface Account { email?: string; name?: string; company?: string; plan_id?: string; status?: string }
interface Template { id: string; name: string; icon: string; description: string; subject: string; body: string }

const TEMPLATES: Template[] = [
  { id: 'custom', name: 'Custom Message', icon: 'pencil', description: 'Free-form plain text email', subject: '', body: '' },
  { id: 'welcome', name: 'Welcome', icon: 'mail', description: 'New account welcome with license key',
    subject: 'Welcome to WPCursor',
    body: 'Hi there,\n\nWelcome to WPCursor! Your account is ready. Your license key is included in your dashboard.\n\nHappy building,\nThe WPCursor Team' },
  { id: 'low_credits', name: 'Low Credits', icon: 'alert', description: 'Credits running low alert',
    subject: 'Your credits are running low',
    body: 'Hi there,\n\nHeads up — your WPCursor credits are running low. Top up to avoid any interruption to your workflow.\n\nThe WPCursor Team' },
  { id: 'credits_exhausted', name: 'Credits Exhausted', icon: 'alert', description: 'All credits used up',
    subject: "You've run out of credits",
    body: 'Hi there,\n\nYou have used all of your WPCursor credits. Add more credits to keep going.\n\nThe WPCursor Team' },
  { id: 'plan_upgraded', name: 'Plan Upgraded', icon: 'chart', description: 'Plan upgrade confirmation',
    subject: 'Your plan has been upgraded',
    body: 'Hi there,\n\nYour WPCursor plan has been upgraded. Enjoy your new limits and features!\n\nThe WPCursor Team' },
  { id: 'credits_purchased', name: 'Credits Added', icon: 'check', description: 'Credit purchase confirmation',
    subject: 'Credits added to your account',
    body: 'Hi there,\n\nYour credit purchase was successful and the credits are now on your account.\n\nThe WPCursor Team' },
  { id: 'site_activated', name: 'Site Activated', icon: 'globe', description: 'WordPress site connected',
    subject: 'Your site is connected',
    body: 'Hi there,\n\nYour WordPress site is now connected to WPCursor. You are all set.\n\nThe WPCursor Team' },
];

const RECURRENCE_LABELS: Record<string, string> = {
  daily: 'Daily', every3days: 'Every 3 days', weekly: 'Weekly', biweekly: 'Bi-weekly', monthly: 'Monthly',
};

function StatusBadge({ status }: { status?: string }) {
  const st = (status || '').toLowerCase();
  const cls =
    st === 'sent' ? 'badge-green'
    : st === 'draft' ? 'badge-gray'
    : st === 'sending' || st === 'paused' ? 'badge-amber'
    : 'badge-purple'; // scheduled, recurring
  if (st === 'failed') {
    return <span className="badge" style={{ background: 'rgba(239,68,68,0.1)', color: '#EF4444' }}>{status || '—'}</span>;
  }
  return <span className={'badge ' + cls}>{status || '—'}</span>;
}

export default function Emails() {
  const { can } = useAuth();
  const canWrite = can('emails', 'write');
  const [campaigns, setCampaigns] = useState<Campaign[]>([]);
  const [segments, setSegments] = useState<Segment[]>([]);
  const [history, setHistory] = useState<LogEntry[]>([]);
  const [totals, setTotals] = useState({ total: 0, sent: 0, failed: 0 });
  const [error, setError] = useState(''); const [note, setNote] = useState('');
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState<Campaign | null>(null);
  const [composeTpl, setComposeTpl] = useState<Template | null>(null);
  const [broadcasting, setBroadcasting] = useState(false);
  const [previewHtml, setPreviewHtml] = useState<string | null>(null);

  async function load() {
    setLoading(true);
    const [c, h, s] = await Promise.all([
      backendApi.get<{ campaigns: Campaign[] }>('/license/admin/campaigns').catch(() => ({ campaigns: [] })),
      backendApi.get<any>('/license/admin/email/history?limit=100').catch(() => ({ history: [] })),
      backendApi.get<any>('/license/admin/campaigns/segments').catch(() => ({ segments: [] })),
    ]);
    setCampaigns(c.campaigns || []);
    const hist = h.history || h.emails || h.log || [];
    setHistory(hist);
    setTotals({
      total: h.total ?? hist.length,
      sent: h.sent ?? hist.filter((e: LogEntry) => (e.status || '').match(/deliver|sent|ok/i)).length,
      failed: h.failed ?? hist.filter((e: LogEntry) => (e.status || '').match(/fail|bounce|error/i)).length,
    });
    setSegments(s.segments || s || []);
    setLoading(false);
  }
  useEffect(() => { void load(); }, []);

  function closeForms() { setComposeTpl(null); setBroadcasting(false); setEditing(null); }

  async function act(c: Campaign, verb: 'send' | 'pause' | 'resume' | 'duplicate') {
    if (verb === 'send' && !confirm(`Send "${c.name || c.subject}" to ${c.recipient_count ?? 'all'} recipients now?`)) return;
    setError(''); setNote('');
    try {
      await backendApi.post(`/license/admin/campaigns/${c.id}/${verb}`);
      setNote(`Campaign ${verb === 'duplicate' ? 'duplicated' : verb + 'd'}.`);
      await load();
    } catch (err: any) { setError(err.body?.error || err.message); }
  }

  async function remove(c: Campaign) {
    if (!confirm(`Delete campaign "${c.name || c.subject}"?`)) return;
    setError(''); setNote('');
    try {
      await backendApi.delete(`/license/admin/campaigns/${c.id}`);
      setNote('Campaign deleted.'); await load();
    } catch (err: any) { setError(err.body?.error || err.message); }
  }

  return (
    <>
      <header className="page-head">
        <div><h1>Email Center</h1><p className="muted">Templates, broadcasts, campaigns and delivery history.</p></div>
        {canWrite && (
          <div className="row-actions">
            <button className="btn" onClick={() => { const open = !!composeTpl; closeForms(); if (!open) setComposeTpl(TEMPLATES[0]); }}>
              {composeTpl ? 'Cancel' : 'Compose Email'}</button>
            <button className="btn" onClick={() => { const open = broadcasting; closeForms(); if (!open) setBroadcasting(true); }}>
              {broadcasting ? 'Cancel' : 'Broadcast'}</button>
            <button className="btn btn-primary" onClick={() => { const open = !!editing; closeForms(); if (!open) setEditing({} as Campaign); }}>
              {editing ? 'Cancel' : 'New Campaign'}</button>
          </div>
        )}
      </header>

      <div className="stat-row">
        <StatCard icon="mail" tint="purple" value={totals.total} label="Emails Sent" />
        <StatCard icon="check" tint="green" value={totals.sent} label="Delivered" />
        <StatCard icon="alert" tint="amber" value={totals.failed} label="Failed" />
        <StatCard icon="tag" tint="gray" value={TEMPLATES.length} label="Templates" />
      </div>

      {error && <div className="alert alert-error">{error}</div>}
      {note && <div className="alert alert-ok">{note}</div>}

      {canWrite && !composeTpl && !broadcasting && !editing && (
        <div className="card">
          <h2 className="card-title">Templates</h2>
          <div className="tpl-grid" style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(min(200px, 100%), 1fr))', gap: 12 }}>
            {TEMPLATES.map((t) => (
              <button key={t.id} className="card" style={{ margin: 0, textAlign: 'left', cursor: 'pointer', display: 'flex', flexDirection: 'column', gap: 6, padding: 16 }}
                onClick={() => { closeForms(); setComposeTpl(t); }}>
                <span style={{ color: 'var(--color-status-info-fg)' }}><Icon name={t.icon} size={20} /></span>
                <span style={{ fontWeight: 600, fontSize: 14 }}>{t.name}</span>
                <span className="muted small">{t.description}</span>
              </button>
            ))}
          </div>
        </div>
      )}

      {composeTpl && <Compose template={composeTpl} onPreview={setPreviewHtml}
        onDone={(m) => { setNote(m); setComposeTpl(null); void load(); }} onError={setError} />}
      {broadcasting && <Broadcast onPreview={setPreviewHtml}
        onDone={(m) => { setNote(m); setBroadcasting(false); void load(); }} onError={setError} />}
      {editing && <CampaignForm campaign={editing} segments={segments} onPreview={setPreviewHtml}
        onDone={(m) => { setNote(m); setEditing(null); void load(); }} onError={setError} />}

      <div className="card">
        <h2 className="card-title">Campaigns</h2>
        {loading ? <p className="muted">Loading…</p> : campaigns.length === 0 ? <p className="muted">No campaigns yet.</p> : (
          <div className="table-wrap">
            <table className="table">
              <thead><tr><th>Campaign</th><th>Segment</th><th>Recipients</th><th>Status</th><th>Scheduled</th>{canWrite && <th>Actions</th>}</tr></thead>
              <tbody>
                {campaigns.map((c) => {
                  const st = (c.status || '').toLowerCase();
                  const seg = segments.find((s) => s.id === c.segment);
                  const recip = (st === 'sent' || st === 'recurring')
                    ? `${c.sent_count ?? 0} / ${c.recipient_count ?? 0}`
                    : String(c.recipient_count ?? 0);
                  return (
                    <tr key={c.id}>
                      <td>{c.name || '—'}<span className="muted small block">{c.subject}</span></td>
                      <td className="muted">{seg?.name || c.segment || 'all'}</td>
                      <td className="mono">{recip}</td>
                      <td><StatusBadge status={c.status} /></td>
                      <td className="muted">
                        {c.scheduled_at ? new Date(c.scheduled_at).toLocaleString() : '—'}
                        {c.recurrence && (
                          <span className="block small" style={{ color: 'var(--color-status-info-fg)' }}>
                            {RECURRENCE_LABELS[c.recurrence] || c.recurrence}{c.run_count ? ` · ${c.run_count} runs` : ''}
                          </span>
                        )}
                      </td>
                      {canWrite && (
                        <td>
                          <div className="row-actions">
                            {(st === 'draft' || st === 'scheduled') && <>
                              <button className="btn-icon" title="Edit" onClick={() => { closeForms(); setEditing(c); }}><Icon name="pencil" size={15} /></button>
                              <button className="btn-icon" title="Send now" onClick={() => act(c, 'send')}><Icon name="mail" size={15} /></button>
                            </>}
                            {st === 'recurring' && <button className="btn-icon" title="Pause" onClick={() => act(c, 'pause')}><Icon name="clock" size={15} /></button>}
                            {st === 'paused' && <button className="btn-icon" title="Resume" onClick={() => act(c, 'resume')}><Icon name="play" size={15} /></button>}
                            {st === 'sent' && <button className="btn-icon" title="Duplicate" onClick={() => act(c, 'duplicate')}><Icon name="copy" size={15} /></button>}
                            <button className="btn-icon danger" title="Delete" onClick={() => remove(c)}><Icon name="trash" size={15} /></button>
                          </div>
                        </td>
                      )}
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <div className="card">
        <h2 className="card-title">Send History</h2>
        {loading ? <p className="muted">Loading…</p> : history.length === 0 ? <p className="muted">No emails sent yet.</p> : (
          <div className="table-wrap">
            <table className="table">
              <thead><tr><th>Recipient</th><th>Subject</th><th>Type</th><th>Status</th><th>Sent</th></tr></thead>
              <tbody>
                {history.slice(0, 50).map((e, i) => (
                  <tr key={e.id || i}>
                    <td>{e.to_email || e.recipient || '—'}</td>
                    <td className="muted">{e.subject || '—'}</td>
                    <td><span className={'badge ' + (e.type === 'broadcast' ? 'badge-purple' : 'badge-gray')}>{e.type || '—'}</span></td>
                    <td><span className={'badge ' + ((e.status || '').match(/fail|bounce|error/i) ? 'badge-amber' : 'badge-green')}>{e.status || '—'}</span></td>
                    <td>{(() => {
                      const src = e.sent_at || e.created_at;
                      const d = src ? new Date(src) : null;
                      if (!d || isNaN(d.getTime())) return <span className="muted">—</span>;
                      return <><div style={{ whiteSpace: 'nowrap' }}>{fmtSentFull(d)}</div>
                        <div className="muted small">{timeAgoLong(d)}</div></>;
                    })()}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {previewHtml !== null && <PreviewModal html={previewHtml} onClose={() => setPreviewHtml(null)} />}
    </>
  );
}

/** Renders server-built email HTML in a script-disabled sandboxed iframe. */
function PreviewModal({ html, onClose }: { html: string; onClose: () => void }) {
  return (
    <div className="modal-scrim" onClick={onClose}>
      <div className="modal" style={{ maxWidth: 680 }} onClick={(e) => e.stopPropagation()}>
        <div className="modal-head">
          <h2 className="card-title" style={{ margin: 0 }}>Email Preview</h2>
          <button type="button" className="btn btn-sm" onClick={onClose}>Close</button>
        </div>
        <iframe sandbox="" title="Email preview" srcDoc={html}
          style={{ width: '100%', height: '55vh', border: '1px solid var(--color-border-subtle)', borderRadius: 8, background: '#fff' }} />
      </div>
    </div>
  );
}

async function fetchPreview(subject: string, body: string, onPreview: (h: string) => void, onError: (s: string) => void) {
  onError('');
  if (!subject.trim() || !body.trim()) { onError('Enter a subject and body before previewing.'); return; }
  try {
    const data = await backendApi.post<{ success: boolean; html: string; error?: string }>(
      '/license/admin/email/preview', { subject: subject.trim(), body });
    if (!data.success) throw new Error(data.error || 'Preview failed');
    onPreview(data.html);
  } catch (err: any) { onError(err.body?.error || err.message); }
}

function Compose({ template, onPreview, onDone, onError }:
  { template: Template; onPreview: (h: string) => void; onDone: (m: string) => void; onError: (s: string) => void }) {
  const [f, setF] = useState({ to: '', subject: template.subject, body: template.body });
  const [busy, setBusy] = useState(false);

  // Re-seed when a different template card is picked while the form is open.
  useEffect(() => { setF({ to: '', subject: template.subject, body: template.body }); }, [template]);

  async function submit(e: FormEvent) {
    e.preventDefault(); setBusy(true); onError('');
    try {
      await backendApi.post('/license/admin/email/send', {
        to: f.to.trim(), subject: f.subject.trim(), body: f.body, template: template.id,
      });
      onDone(`Email sent to ${f.to}.`);
    } catch (err: any) { onError(err.body?.detail || err.body?.error || err.message); }
    finally { setBusy(false); }
  }

  return (
    <form className="card" onSubmit={submit}>
      <h2 className="card-title">Compose: {template.name}</h2>
      <div className="form-row">
        <label className="field"><span>To</span>
          <input required type="email" placeholder="customer@example.com" value={f.to} onChange={(e) => setF({ ...f, to: e.target.value })} /></label>
        <label className="field"><span>Subject</span>
          <input required value={f.subject} onChange={(e) => setF({ ...f, subject: e.target.value })} /></label>
      </div>
      <label className="field"><span>Message</span>
        <textarea className="textarea" required value={f.body} onChange={(e) => setF({ ...f, body: e.target.value })} /></label>
      <div className="row-actions">
        <button className="btn btn-primary" disabled={busy}>{busy ? 'Sending…' : 'Send Email'}</button>
        <button type="button" className="btn" onClick={() => fetchPreview(f.subject, f.body, onPreview, onError)}>
          <Icon name="eye" size={15} /> Preview</button>
      </div>
    </form>
  );
}

function Broadcast({ onPreview, onDone, onError }:
  { onPreview: (h: string) => void; onDone: (m: string) => void; onError: (s: string) => void }) {
  const [accounts, setAccounts] = useState<Account[] | null>(null);
  const [f, setF] = useState({ subject: '', body: '', plan: '', status: '', search: '' });
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    backendApi.get<{ accounts: Account[] }>('/license/admin/accounts')
      .then((d) => setAccounts(d.accounts || []))
      .catch(() => setAccounts([]));
  }, []);

  const plans = useMemo(() => [...new Set((accounts || []).map((a) => a.plan_id).filter(Boolean))] as string[], [accounts]);
  const statuses = useMemo(() => [...new Set((accounts || []).map((a) => a.status).filter(Boolean))] as string[], [accounts]);

  // Mirror the backend broadcast filter so the recipient count matches what will send.
  const recipients = useMemo(() => {
    if (!accounts) return null;
    const q = f.search.trim().toLowerCase();
    return accounts.filter((a) =>
      (!f.plan || a.plan_id === f.plan) &&
      (!f.status || a.status === f.status) &&
      (!q || [a.email, a.name, a.company].some((v) => v && v.toLowerCase().includes(q))));
  }, [accounts, f.plan, f.status, f.search]);

  const filterDesc = [f.plan, f.status, f.search && `"${f.search}"`].filter(Boolean).join(', ') || 'all';

  async function submit(e: FormEvent) {
    e.preventDefault(); onError('');
    const count = recipients?.length ?? 0;
    if (count === 0) { onError('No recipients match the current filters.'); return; }
    if (!confirm(`Send this broadcast to ${count} recipients (${filterDesc})?`)) return;
    setBusy(true);
    const filters: Record<string, string> = {};
    if (f.plan) filters.plan = f.plan;
    if (f.status) filters.status = f.status;
    if (f.search.trim()) filters.search = f.search.trim();
    try {
      const data = await backendApi.post<{ sent: number; failed: number }>('/license/admin/email/broadcast', {
        subject: f.subject.trim(), body: f.body,
        filters: Object.keys(filters).length ? filters : undefined,
      });
      onDone(`Broadcast sent: ${data.sent} delivered, ${data.failed} failed.`);
    } catch (err: any) { onError(err.body?.error || err.message); }
    finally { setBusy(false); }
  }

  return (
    <form className="card" onSubmit={submit}>
      <h2 className="card-title">Broadcast Email</h2>
      <p className="muted small" style={{ marginBottom: 12 }}>
        {recipients === null ? 'Loading recipients…'
          : `${recipients.length} recipients (${filterDesc})`}
      </p>
      <div className="form-row">
        <label className="field"><span>Plan filter</span>
          <select value={f.plan} onChange={(e) => setF({ ...f, plan: e.target.value })}>
            <option value="">All plans</option>
            {plans.map((p) => <option key={p} value={p}>{p}</option>)}
          </select></label>
        <label className="field"><span>Status filter</span>
          <select value={f.status} onChange={(e) => setF({ ...f, status: e.target.value })}>
            <option value="">All statuses</option>
            {statuses.map((s) => <option key={s} value={s}>{s}</option>)}
          </select></label>
        <label className="field"><span>Search (email / name / company)</span>
          <input value={f.search} onChange={(e) => setF({ ...f, search: e.target.value })} placeholder="optional" /></label>
        <label className="field"><span>Subject</span>
          <input required value={f.subject} onChange={(e) => setF({ ...f, subject: e.target.value })} /></label>
      </div>
      <label className="field"><span>Message</span>
        <textarea className="textarea" required value={f.body} onChange={(e) => setF({ ...f, body: e.target.value })} /></label>
      <div className="row-actions">
        <button className="btn btn-primary" disabled={busy || !recipients?.length}>
          {busy ? 'Sending…' : `Send to ${recipients?.length ?? 0}`}</button>
        <button type="button" className="btn" onClick={() => fetchPreview(f.subject, f.body, onPreview, onError)}>
          <Icon name="eye" size={15} /> Preview</button>
      </div>
    </form>
  );
}

function CampaignForm({ campaign, segments, onPreview, onDone, onError }:
  { campaign: Campaign; segments: Segment[]; onPreview: (h: string) => void; onDone: (m: string) => void; onError: (s: string) => void }) {
  const isEdit = Boolean(campaign.id);
  const [f, setF] = useState({
    name: campaign.name || '', subject: campaign.subject || '', body: campaign.body || '',
    segment: campaign.segment || 'all', recurrence: campaign.recurrence || '',
    scheduledAt: campaign.scheduled_at ? campaign.scheduled_at.slice(0, 16) : '',
  });
  const [busy, setBusy] = useState(false);
  const [segPreview, setSegPreview] = useState('');

  // Live recipient preview for the chosen segment.
  useEffect(() => {
    let alive = true;
    const seg = f.segment || 'all';
    setSegPreview('Loading recipients…');
    backendApi.get<{ count: number; preview: { email: string }[] }>(`/license/admin/campaigns/segments/${seg}/preview`)
      .then((d) => {
        if (!alive) return;
        const names = (d.preview || []).slice(0, 5).map((r) => r.email).join(', ');
        const more = d.count > 5 ? ` +${d.count - 5} more` : '';
        setSegPreview(`${d.count} recipients${names ? `: ${names}${more}` : ''}`);
      })
      .catch(() => { if (alive) setSegPreview(''); });
    return () => { alive = false; };
  }, [f.segment]);

  function payload() {
    return {
      name: f.name.trim(), subject: f.subject.trim(), body: f.body,
      segment: f.segment || 'all',
      recurrence: f.recurrence || null,
      scheduledAt: f.scheduledAt ? new Date(f.scheduledAt).toISOString() : null,
    };
  }

  async function save(): Promise<string> {
    if (isEdit) { await backendApi.put(`/license/admin/campaigns/${campaign.id}`, payload()); return campaign.id!; }
    const res = await backendApi.post<{ campaign: { id: string } }>('/license/admin/campaigns', payload());
    return res.campaign.id;
  }

  async function submit(e: FormEvent) {
    e.preventDefault(); setBusy(true); onError('');
    try {
      await save();
      onDone(isEdit ? 'Campaign updated.' : f.scheduledAt ? 'Campaign scheduled.' : 'Campaign saved as draft.');
    } catch (err: any) { onError(err.body?.error || err.message); }
    finally { setBusy(false); }
  }

  // Save-then-send in one step (legacy sendCampaignNow).
  async function sendNow() {
    if (!f.name.trim() || !f.subject.trim() || !f.body.trim()) { onError('Name, subject, and body are required.'); return; }
    setBusy(true); onError('');
    try {
      const id = await save();
      await backendApi.post(`/license/admin/campaigns/${id}/send`);
      onDone('Campaign sending now.');
    } catch (err: any) { onError(err.body?.error || err.message); }
    finally { setBusy(false); }
  }

  return (
    <form className="card" onSubmit={submit}>
      <h2 className="card-title">{isEdit ? 'Edit campaign' : 'New Campaign'}</h2>
      <div className="form-row">
        <label className="field"><span>Campaign Name</span>
          <input required value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} /></label>
        <label className="field"><span>Subject</span>
          <input required value={f.subject} onChange={(e) => setF({ ...f, subject: e.target.value })} /></label>
        <label className="field"><span>Audience Segment</span>
          <select value={f.segment} onChange={(e) => setF({ ...f, segment: e.target.value })}>
            {segments.length === 0 && <option value="all">All Users</option>}
            {segments.map((s) => <option key={s.id} value={s.id}>{s.name || s.id}{s.count != null ? ` (${s.count})` : ''}</option>)}
          </select></label>
        <label className="field"><span>Recurrence</span>
          <select value={f.recurrence} onChange={(e) => setF({ ...f, recurrence: e.target.value })}>
            <option value="">One-time</option>
            {Object.entries(RECURRENCE_LABELS).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
          </select></label>
        <label className="field"><span>First Send (optional)</span>
          <input type="datetime-local" value={f.scheduledAt} onChange={(e) => setF({ ...f, scheduledAt: e.target.value })} /></label>
      </div>
      {segPreview && <p className="muted small" style={{ marginBottom: 12 }}>{segPreview}</p>}
      <label className="field"><span>Body</span>
        <textarea className="textarea" required value={f.body} onChange={(e) => setF({ ...f, body: e.target.value })} /></label>
      <div className="row-actions">
        <button className="btn btn-primary" disabled={busy}>{busy ? 'Saving…' : isEdit ? 'Save changes' : 'Save Draft'}</button>
        <button type="button" className="btn" disabled={busy} onClick={sendNow}><Icon name="mail" size={15} /> Send now</button>
        <button type="button" className="btn" onClick={() => fetchPreview(f.subject, f.body, onPreview, onError)}>
          <Icon name="eye" size={15} /> Preview</button>
      </div>
    </form>
  );
}
